import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Album, AlbumDetail } from '../../model/domain/album';
import { translate } from '../../../I18n/i18n';
import './AlbumsView.scss'
import TaskAlbums from '../../ws/TaskAlbums';
import { Button, Role } from '@cuatroochenta/co-admin-react-library';
import { Media } from '../../model/domain/media';
import PopUpImages from '../common/components/PopUpImages/PopUpImages';
import PopUpRemove from '../common/components/PopUpRemove/PopUpRemove';
import { User } from '../../model/domain/user';
import PopUpAlbums from './components/PopUpAlbums';
import { EmptyContentText } from '../common/components/EmptyContentText/EmptyContentText';
import { ShareCenterOption } from '../../model/domain/share-option';

interface AlbumsViewProps extends WithTranslation {
    albums: Album[]
    user: User
    updateView: () => void
}

interface AlbumsViewState {
    showPopupCreateOrEdit: boolean
    albumToEdit?: AlbumDetail
    showPopupRemove: boolean
    albumIdToRemove?: number
    showPopupImages: boolean
    selectedImageIndex: number
    mediaArray: Array<Media>
    loadingAlbumDetail: boolean
}

class AlbumsView extends React.Component<AlbumsViewProps, AlbumsViewState> {
    private taskAlbums: TaskAlbums;

    constructor(props: AlbumsViewProps) {
        super(props);
        this.taskAlbums = new TaskAlbums();
        this.state = {
            showPopupCreateOrEdit: false,
            showPopupImages: false,
            showPopupRemove: false,
            selectedImageIndex: 0,
            mediaArray: [],
            loadingAlbumDetail: false
        };
    }

    componentDidUpdate(prevProps: Readonly<AlbumsViewProps>, prevState: Readonly<AlbumsViewState>, snapshot?: any): void {
        const { loadingAlbumDetail } = this.state;
        document.body.style.overflow = loadingAlbumDetail
            ? 'hidden'
            : '';
    }

    private getAlbumCoverUrl(album: Album) {
        return new TaskAlbums().getAlbumCoverUrl(album);
    }

    private togglePopupCreateOrEdit = async (album?: Album) => {
        try {
            console.log(album)
            let albumToEdit;
            if (album) {
                this.setState({ loadingAlbumDetail: true });
                albumToEdit = await this.taskAlbums.getAlbumDetail(album.id);
                this.setState({ loadingAlbumDetail: false });
            }
            this.setState(prevState => ({
                showPopupCreateOrEdit: !prevState.showPopupCreateOrEdit,
                albumToEdit: albumToEdit
            }));
        } catch (err) {
            this.setState({ loadingAlbumDetail: false });
            console.log('Error obteniendo detalle de álbum', err);
            alert(translate(this.props.t, 'albums.TR_ERROR_GETTING_ALBUM_DETAIL'));
        }
    }

    private togglePopupRemove(albumId: number) {
        this.setState(prevState => ({
            showPopupRemove: !prevState.showPopupRemove,
            albumIdToRemove: albumId
        }));
    }
    
    private togglePopupImages = (index: number, mediaArray: Array<Media>) => {
        this.setState(prevState => ({
            showPopupImages: !prevState.showPopupImages,
            selectedImageIndex: prevState.showPopupImages
                ? -1
                : index,
            mediaArray
        }));
    }

    private handlePopupAlbums = (postContent: { 
        title: string,
        media: File[],
        isForAllCenters: string,
        isCreate: boolean,
        shareOptions: ShareCenterOption[]
    }) => {
        const { albumToEdit } = this.state;
        const isCreate = postContent.isCreate; // Usar isCreate pasado desde el PopUpAlbums

        // Crear el objeto con los datos del post que se van a enviar
        const postData = {
            title: postContent.title,
            shareOptions: postContent.shareOptions,
            isForAllCenters: postContent.isForAllCenters,  // Se mantiene como string 'true'/'false'
            // Se maneja media dependiendo si es un archivo o un arreglo
            media: Array.isArray(postContent.media) && postContent.media.length > 0
                ? postContent.media
                : postContent.media instanceof File
                    ? [postContent.media]  // Si es un solo archivo (video), lo pasamos como un arreglo
                    : undefined
        };

        if (isCreate) {
            // Crear un nuevo post
            this.taskAlbums.createAlbum(postData)
                .then(() => {
                    this.setState({ showPopupCreateOrEdit: false, albumToEdit: undefined });
                    window.location.reload(); // Recargar la página para mostrar el nuevo post
                })
                .catch(error => {
                    console.error('Error al crear el post:', error);
                    alert(translate(this.props.t, 'common.TR_ERROR_CREATING_POST') + error.message);
                });
        } else {
            // Editar un post existente
            if (albumToEdit) {
                const updatedPostData = {
                    ...postData,
                    id: albumToEdit.id, // Añadir el id del post a editar
                };

                this.taskAlbums.editAlbum(albumToEdit.id, updatedPostData)
                    .then(() => {
                        console.log('Post editado');
                        this.props.updateView();
                        this.setState({ showPopupCreateOrEdit: false, albumToEdit: undefined });
                    })
                    .catch(error => {
                        console.error('Error al editar el post:', error);
                        alert(translate(this.props.t, 'common.TR_ERROR_EDITING_POST') + error.message);
                    });
            }
        }
    }

    private handleViewAlbum = async (event: React.MouseEvent<HTMLElement>, albumId: number) => {
        event.stopPropagation();

        try {
            this.setState({ loadingAlbumDetail: true });
            const album = await this.taskAlbums.getAlbumDetail(albumId);
            this.setState({ loadingAlbumDetail: false });
            this.togglePopupImages(0, album.media);
        } catch (err) {
            this.setState({ loadingAlbumDetail: false });
            console.error('Error al obtener el detalle de álbum:', err);
        }
    }

    private handleRemoveAlbum = () => {
        const { albumIdToRemove } = this.state;

        if (albumIdToRemove) {
            this.taskAlbums.removeAlbum(albumIdToRemove)
                .then(() => {
                    console.log('Álbum eliminado');
                    this.setState({ showPopupRemove: false, albumIdToRemove: 0 });
                    setTimeout(() => {
                        window.location.reload();
                    }, 0);
                })
                .catch((error: any) => {
                    console.error('Error al eliminar el álbum:', error);
                    this.setState({ showPopupRemove: false, albumIdToRemove: 0 });
                    alert(translate(this.props.t, 'albums.TR_ERROR_DELETING_ALBUM') + error.message);
                });
        }
    }

    private renderAlbum(album: Album): React.ReactNode {
        const { t, user } = this.props;

        return (
            <div className="card-album shadow mb-3" key={album.id}>
                <div className="card-body">
                    <div className="card-container-header">
                        <div className="card-container-text-header">
                            <p className="card-text-bold">{album.title}</p>
                            <p className="card-text-date text-muted">
                                <small>{album.date}</small>
                            </p>
                            <p className="card-text-date text-muted">
                                <small>{translate(t, "albums.TR_MEDIA_COUNT_LABEL", { count: album.mediaCount })}</small>
                            </p>
                        </div>
                        {(user.data.roles.includes(Role.SUPERADMIN) || user.data.id === album.userId) && (
                            <div className="card-actions">
                                <button className="action-btn edit-btn" onClick={() => this.togglePopupCreateOrEdit(album)}>
                                    <i className="fas fa-edit"></i>
                                </button>
                                <button className="action-btn delete-btn" onClick={() => this.togglePopupRemove(album.id)}>
                                    <i className="fas fa-trash-alt"></i>
                                </button>
                            </div>
                        )}
                    </div>

                    {album.coverId && (
                        <div className="media-container single-item">
                            <div className="media-item">
                                <div
                                    className="media-image-container"
                                    onClick={(event) => this.handleViewAlbum(event, album.id)}
                                >
                                    <img
                                        className="media-image"
                                        src={this.getAlbumCoverUrl(album)}
                                        alt=""
                                    />
                                    {album.mediaCount > 1 && (
                                        <div className="media-count-container">
                                            <span className="media-count-text">+{album.mediaCount - 1}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    public render() {
        const { t, user, albums } = this.props
        const {
            showPopupImages,
            mediaArray,
            selectedImageIndex,
            showPopupRemove,
            showPopupCreateOrEdit,
            albumToEdit,
            loadingAlbumDetail
        } = this.state

        return (
            <div className="container">
                <Button 
                    content={translate(t, 'albums.TR_CREATE_LABEL')}
                    onClick={() => this.togglePopupCreateOrEdit(undefined)}
                />

                <PopUpAlbums
                    show={showPopupCreateOrEdit}
                    onClose={() => this.togglePopupCreateOrEdit(undefined)}
                    onSubmit={this.handlePopupAlbums}
                    album={albumToEdit}
                    task={this.taskAlbums}
                    user={user}
                />

                <PopUpImages
                    show={showPopupImages}
                    onClose={() => this.togglePopupImages(0, [])}
                    mediaArray={mediaArray}
                    selectedImageIndex={selectedImageIndex}
                />

                <PopUpRemove
                    show={showPopupRemove}
                    onClose={() => this.setState({ showPopupRemove: false, albumIdToRemove: 0 })}
                    onSubmit={this.handleRemoveAlbum}
                />

                {albums.length === 0 ? (
                    <EmptyContentText
                        message={translate(t, 'albums.TR_NO_POSTS_LABEL')}
                    />
                ) : (
                    albums.map(album => this.renderAlbum(album))
                )}

                {loadingAlbumDetail ? (
                    <div className="loading-album-container">
                        <p className="loading-album-text">{translate(t, 'albums.TR_LOADING_LABEL')}</p>
                    </div>
                ) : null}
            </div>
        )
    }
}

export default withTranslation()(AlbumsView);